import { useCallback, useState } from 'react';
import { useContextAwareDownloadProjectReport, useContextAwareLazyGetFastActivitiesByProject } from '@hooks/index';
import { useTriggerAsyncReportMutation } from '@app/features/project/projectApiSlice';
import { useAppSelector } from '@app/hooks';
import { extractErrorMessage } from '@utils/useErrorMessage';
import { isFetchBaseQueryError } from '@utils/helperFunctions';
import type { ErrorType, FastActivity } from '@typedefs/interfaces';
import type { ReportAsyncResponse } from '@app/features/asyncJobs/asyncJobsTypes';
import type { ReportTemplate } from '@app/features/project/projectTypes';
import i18n from 'src/i18n';

const ASYNC_THRESHOLD = 40;

interface UseDownloadReportProps {
  projectId: number;
  projectName?: string;
  activities?: number[];
}

interface UseDownloadReportReturn {
  downloadExcel: () => Promise<void>;
  downloadNarrativeReport: (template: ReportTemplate, content?: string) => Promise<void>;
  isLoading: boolean;
  errorMsg: string | null;
  isOpen: boolean;
  handleCancel: () => void;
  emailNoticeOpen: boolean;
  closeEmailNotice: () => void;
  readinessOpen: boolean;
  notReadyActivities: FastActivity[];
  closeReadinessModal: () => void;
}

const useDownloadReport = ({ projectId, projectName, activities }: UseDownloadReportProps): UseDownloadReportReturn => {
  // Fallback names follow the backend convention ({project name}_…); used until Content-Disposition is readable.
  const fileBaseName = projectName || String(projectId);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [emailNoticeOpen, setEmailNoticeOpen] = useState(false);
  const [readinessOpen, setReadinessOpen] = useState(false);
  const [notReadyActivities, setNotReadyActivities] = useState<FastActivity[]>([]);
  
  const [triggerSyncDownload] = useContextAwareDownloadProjectReport();
  const [fetchNotReadyActivities] = useContextAwareLazyGetFastActivitiesByProject();
  const [triggerAsyncReport] = useTriggerAsyncReportMutation();
  
  const project = useAppSelector(state => 
    state.api?.queries?.[`getProjectById("${projectId}")`]?.data as { activity_count?: string; module_count?: string } | undefined
  );

  const handleCancel = () => {
    setIsOpen(false);
    setErrorMsg(null);
  };

  const closeEmailNotice = () => {
    setEmailNoticeOpen(false);
  };

  const closeReadinessModal = () => {
    setReadinessOpen(false);
    setNotReadyActivities([]);
  };

  const isSmallProject = useCallback(() => {
    if (!project) return true;
    
    const activityCount = parseInt(project.activity_count || '0', 10);
    const moduleCount = parseInt(project.module_count || '0', 10);
    return activityCount + moduleCount <= ASYNC_THRESHOLD;
  }, [project]);

  const handleDownload = useCallback(async (options: {
    template?: string;
    fileName: string;
    content?: string;
    language?: string;
  }) => {
    const { template, fileName, content, language = i18n.language } = options;
    
    if (!projectId) return;
    setIsLoading(true);
    setErrorMsg(null);
    setEmailNoticeOpen(false);
    setReadinessOpen(false);
    setNotReadyActivities([]);
    
    try {
      try {
        const response = await fetchNotReadyActivities({
          projectId,
          ready: false,
          page: 1,
          pageSize: 100,
        }).unwrap();

        const selectedActivityIds = activities ? new Set(activities) : null;
        // The public activities endpoint ignores `ready`, so re-check completion here.
        const includedNotReadyActivities = response.results.filter(
          activity => activity.completion_percentage < 1
            && !activity.is_b_intact
            && (!selectedActivityIds || selectedActivityIds.has(activity.id))
        );

        if (includedNotReadyActivities.length > 0) {
          setNotReadyActivities(includedNotReadyActivities);
          setReadinessOpen(true);
          return;
        }
      } catch {
        // The report endpoint also validates readiness, so continue when the pre-check fails.
      }

      if (isSmallProject()) {
        const response = await triggerSyncDownload({
          id: projectId,
          activities,
          ...(template && { template }),
          ...(content && { content }),
          currentLanguage: language,
        });

        if ('error' in response) {
          if (isFetchBaseQueryError(response.error) && response.error.data instanceof Blob) {
            const errorText = await response.error.data.text();
            const errorJson = JSON.parse(errorText);
            setErrorMsg(errorJson.details || 'An error occurred while downloading the report');
          } else {
            const errMsg = extractErrorMessage(response.error as ErrorType);
            setErrorMsg(errMsg || 'An error occurred while downloading the report');
          }
          setIsOpen(true);
          return;
        }

        const { data } = response;
        if (!data) {
          setErrorMsg('An error occurred while downloading the report');
          setIsOpen(true);
          return;
        }

        const url = window.URL.createObjectURL(data.blob);
        const link = document.createElement('a');
        link.href = url;
        // Prefer the backend's name; ours is the fallback until it exposes Content-Disposition.
        link.setAttribute('download', data.fileName ?? fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode?.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        await triggerAsyncReport({
          id: projectId,
          activities,
          ...(template && { template }),
          ...(content && { content }),
          currentLanguage: language,
        }).unwrap() as ReportAsyncResponse;

        setEmailNoticeOpen(true);
      }
    } catch (error) {
      console.error('Error downloading the report:', error);
      setErrorMsg('An unexpected error occurred while downloading the report');
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, [projectId, activities, fetchNotReadyActivities, isSmallProject, triggerSyncDownload, triggerAsyncReport]);

  const downloadExcel = useCallback(() => {
    return handleDownload({
      fileName: `${fileBaseName}_report.xlsx`,
    });
  }, [handleDownload, fileBaseName]);

  const downloadNarrativeReport = useCallback(({ id, languages }: ReportTemplate, content?: string) => {
    // The backend rejects a template in a language it doesn't list (400), so fall back to its first one.
    const language = !languages.length || languages.includes(i18n.language) ? i18n.language : languages[0];
    return handleDownload({
      template: id,
      fileName: `${fileBaseName}_${id}_report.pdf`,
      content,
      language,
    });
  }, [handleDownload, fileBaseName]);

  return {
    isLoading,
    downloadExcel,
    downloadNarrativeReport,
    handleCancel,
    isOpen,
    errorMsg,
    emailNoticeOpen,
    closeEmailNotice,
    readinessOpen,
    notReadyActivities,
    closeReadinessModal,
  };
};

export default useDownloadReport;
